import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { KpiLayout } from "components/OverviewPages/KPIs/KpiLayout";
import Tooltip from "@mui/material/Tooltip";

import { useLazyFetchGet } from "hooks/fetch/useLazyFetchGet";

import { ReactComponent as MachineIcon } from "assets/img/KPIIcons/machines.svg";

import { styled } from "@mui/material/styles";
import { FeaturesContext } from "contexts/FeaturesContext";
import { EntitlementContext } from "contexts/UserProfileContext";
import {
  DEFAULT_KPI_FILTERS,
  DEFAULT_SORT
} from "components/DownDerateTrackerTable/DownDerateTrackerTableColumns";
import { useDownDerateTrackerData } from "contexts/DownDerateDataContext";
import { useHistory } from "react-router-dom";

export function MachinesDownKpi({ expanded, fleet, site }) {
  const [downedMachines, setDownedMachines] = useState();
  const [deratedMachines, setDeratedMachines] = useState();

  const { setTableState } = useDownDerateTrackerData();

  const history = useHistory();
  const { hasFeature } = useContext(FeaturesContext);
  const { hasEntitlement } = useContext(EntitlementContext);

  const url = site?.id
    ? `machines/status/summary?site_id=${site.id}`
    : "machines/status/summary";

  const { loading, getCallback } = useLazyFetchGet(
    url,
    machineStatusSummary => {
      setDownedMachines(
        machineStatusSummary?.find(({ status }) => status === 0)?.count ?? 0
      );
      setDeratedMachines(
        machineStatusSummary?.find(({ status }) => status === 2)?.count ?? 0
      );
    }
  );

  useEffect(() => {
    if (
      expanded &&
      downedMachines === undefined &&
      deratedMachines === undefined
    ) {
      if (fleet || site) {
        getCallback();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deratedMachines, downedMachines, fleet, expanded, site]);

  const onKpiClicked = () => {
    setTableState(state => ({
      ...state,
      sortBy: [DEFAULT_SORT],
      filters: {
        ...DEFAULT_KPI_FILTERS,
        Site: site?.name && site?.id ? [{ name: site.name, id: site.id }] : []
      }
    }));
    history.push("/pages/down-derate-tracker");
  };
  const downDerateEnabled = useMemo(
    () =>
      hasFeature("down_derated_tracker") &&
      hasEntitlement("downderatedtracker-view"),
    [hasEntitlement, hasFeature]
  );

  return (
    <KpiLayout
      title="Down/Derated Machines"
      loading={
        loading ||
        typeof downedMachines !== "number" ||
        typeof deratedMachines !== "number"
      }
      Icon={MachineIcon}
      cursorEnable={downDerateEnabled}
      onClickEvent={downDerateEnabled ? onKpiClicked : undefined}
    >
      <Tooltip
        title="Down (Left) / Derated (Right)"
        aria-label="down-derated-kpi-tooltip"
      >
        <KpiBody>
          <KpiMachineCount>{downedMachines}</KpiMachineCount>
          <KpiSlash>&nbsp; / &nbsp;</KpiSlash>
          <KpiMachineCount>{deratedMachines}</KpiMachineCount>
        </KpiBody>
      </Tooltip>
    </KpiLayout>
  );
}

const KpiBody = styled("div")`
  font-weight: 600;
  font-size: 32px;
  line-height: 48px;
  height: 48px;
`;

const KpiMachineCount = styled("span")`
  color: #485057;
`;

const KpiSlash = styled("span")`
  color: #000;
`;

MachinesDownKpi.propTypes = {
  expanded: PropTypes.bool.isRequired,
  fleet: PropTypes.bool,
  site: PropTypes.object
};
